import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Stethoscope, Disc3, Cog, CircleDot, Droplets, Snowflake, ShieldCheck, BookOpen, ArrowRight, Filter } from "lucide-react";

const ICONS = { Stethoscope, Disc3, Cog, CircleDot, Droplets, Snowflake, ShieldCheck, BookOpen };

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/categories")])
      .then(([s, c]) => {
        setServices(s.data);
        setCategories(c.data);
      })
      .catch(() => {});
  }, []);

  // Build chips: "All" + categories that actually have ≥1 service + "Other" if any uncategorised exist
  const chips = useMemo(() => {
    const usedCats = new Set(services.map((s) => (s.category || "").trim()).filter(Boolean));
    const orderedCats = categories.filter((c) => usedCats.has(c.name)).map((c) => c.name);
    const hasOther = services.some((s) => !s.category || !categories.find((c) => c.name === s.category));
    const all = [{ id: "all", label: `All (${services.length})` }, ...orderedCats.map((n) => ({ id: n, label: n }))];
    if (hasOther) all.push({ id: "__other", label: "Other" });
    return all;
  }, [services, categories]);

  const filtered = useMemo(() => {
    if (active === "all") return services;
    if (active === "__other") {
      return services.filter((s) => !s.category || !categories.find((c) => c.name === s.category));
    }
    return services.filter((s) => s.category === active);
  }, [services, categories, active]);

  return (
    <div data-testid="services-page" className="bg-background">
      <section className="border-b border-brand-red/20 bg-zinc-950/30 hud-grid-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-red blink-dot" />
            <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Services &amp; pricing · Live feed</span>
          </div>
          <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[0.9]">
            Every workshop service,<br /><span className="text-brand-red">priced upfront.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300">
            All prices are starting points for standard passenger vehicles. We&apos;ll quote your exact car before
            we lift a spanner — no surprises, no hidden labour.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
        {chips.length > 1 && (
          <div data-testid="services-filter-bar" className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-brand-red" />
            {chips.map((chip) => {
              const isActive = active === chip.id;
              return (
                <button
                  key={chip.id}
                  data-testid={`services-filter-${chip.id}`}
                  onClick={() => setActive(chip.id)}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors ${
                    isActive
                      ? "border-brand-red text-brand-red bg-brand-red/10"
                      : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {filtered.length === 0 ? (
          <div data-testid="services-empty" className="border border-zinc-900 bg-zinc-950 p-12 text-center text-zinc-400 font-mono text-xs uppercase tracking-widest">
            No services in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((s, i) => {
              const Icon = ICONS[s.icon] || Cog;
              return (
                <article key={s.id} data-testid={`service-card-${s.id}`} className="hud-panel rounded-xl p-8 flex gap-6 transition-all duration-300 hover:shadow-[0_0_30px_rgba(225,29,43,0.18)] hover:-translate-y-0.5">
                  <div className="h-14 w-14 border border-brand-red/40 bg-brand-red/10 rounded grid place-items-center shrink-0">
                    <Icon className="h-6 w-6 text-brand-red" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-display uppercase text-2xl text-white tracking-tight">{s.title}</h3>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">/0{i + 1}</span>
                    </div>
                    {s.category && (
                      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                        {s.category}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.description}</p>
                    <div className="mt-5 flex items-center gap-6 border-t border-zinc-900 pt-4">
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">From</div>
                        <div className="font-display text-2xl text-brand-red">${s.price_from}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Time</div>
                        <div className="font-display text-2xl text-white">{s.duration_min}<span className="text-zinc-500 text-sm">m</span></div>
                      </div>
                      <Link to={`/book?service=${s.id}`} data-testid={`book-service-${s.id}`} className="ml-auto inline-flex items-center gap-2 px-5 py-3 bg-brand-red text-white font-bold uppercase text-xs tracking-wider hover:bg-[#B91C2B] glow-btn rounded">
                        Book <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="services-seo-copy">
        <div className="border-t border-zinc-900 pt-10 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl">
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">Logbook servicing that protects your warranty</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Under Australian consumer law you don't have to return to the dealership to keep your new-car warranty —
              you just need a qualified workshop that services to the manufacturer's schedule with quality parts and
              fluids. That's exactly what we do. As a Repco Authorised Service centre we stamp your logbook at every
              visit, record the work in your digital service history, and remind you by SMS and email when the next
              service is coming up. Every job is backed by a nationwide Repco warranty.
            </p>
          </div>
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">One workshop for the whole job</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              From a quick oil change to engine rebuilds, we handle the full mechanical stack under one roof in
              Glenorchy: computer diagnostics, brakes, suspension, clutch, air conditioning, tyres and wheel
              alignment, roadworthy inspections and auto electrical work — for modern daily drivers and classic cars
              alike. Prices shown are honest starting points; we confirm the exact quote in writing before any work
              begins, and nothing happens without your sign-off. Drivers come to us from Moonah, Claremont, Montrose,
              Rosetta, New Town and right across greater Hobart.
            </p>
          </div>
        </div>
        <div className="mt-10 border border-zinc-900 bg-zinc-950 rounded-xl p-5 max-w-5xl" data-testid="services-guides-links">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">In-depth service guides</p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
            <Link to="/services/brake-repair" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Brake repair Glenorchy</Link>
            <Link to="/services/logbook-servicing" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Logbook servicing</Link>
            <Link to="/services/car-diagnostics" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Car diagnostics</Link>
            <Link to="/services/tyres-wheel-alignment" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Tyres &amp; wheel alignment</Link>
            <Link to="/services/air-conditioning" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Air conditioning</Link>
            <Link to="/services/roadworthy-certificate" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Roadworthy certificate</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
